import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { STATIC_ROOT } from "Config";
import { notification } from "top-component-library";
import { generalErrorAlert } from "util/Utils";

import { ASSET_SAVE_ENDPOINTS, GUEST_MESSAGE_GROUPS } from "./constants";
import { AllowedCustomer, AppProperties, AssetType, GuestMessage, OwnerProperties, SelectedCustomer } from "./types";

// Stored paths already start with "/customer/<id>/...", so this only needs
// STATIC_ROOT without its trailing slash (matches the AngularJS page, which
// prefixes with customer_upload_root, not STATIC_ROOT + "customer").
const customerFileUrl = (path?: string | null): string =>
  path && path !== "None" ? `${STATIC_ROOT.replace(/\/$/, "")}${path}` : "";

const seedMessages = (appProperties: AppProperties): GuestMessage[] => {
  const stored = appProperties?.pre_checkout_instructions ?? {};
  return GUEST_MESSAGE_GROUPS.flatMap((group) =>
    group.messages.map((message) => ({
      id: message.id,
      name: message.name,
      message: stored[message.id] ?? "",
    }))
  );
};

export const usePresentationSettings = (selectedCustomer: SelectedCustomer) => {
  const customerId = selectedCustomer?.customer_id;

  const [properties, setProperties] = useState<OwnerProperties | null>(null);
  const [allowedCustomerIds, setAllowedCustomerIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [messages, setMessages] = useState<GuestMessage[]>([]);
  const [storeHours, setStoreHours] = useState("");
  const [orderNumberPrefix, setOrderNumberPrefix] = useState("");
  const [statementDescriptor, setStatementDescriptor] = useState("");
  const [appProperties, setAppProperties] = useState<AppProperties>({});
  const [sendSmsDrinkReminder, setSendSmsDrinkReminder] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await axios.get("api/getOwnerProperties", { params: { customer_id: customerId } });
      setProperties(res.data.owner_properties as OwnerProperties);
    } catch (error) {
      generalErrorAlert(error, "Error retrieving owner properties, please refresh.");
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const loadAllowedCustomers = async () => {
      try {
        const res = await axios.get("/owner/getAllowedCustomersLight");
        const customers: AllowedCustomer[] = res.data.customers ?? [];
        setAllowedCustomerIds(customers.map((customer) => customer.customer_id));
      } catch (error) {
        generalErrorAlert(error, "There was an error getting allowed customers, please refresh.");
      }
    };
    loadAllowedCustomers();
  }, []);

  // Re-seed the editable local state whenever fresh properties arrive.
  useEffect(() => {
    if (!properties) return;
    setMessages(seedMessages(properties.app_properties));
    setStoreHours(properties.store_hours ?? "");
    setOrderNumberPrefix(properties.order_number_prefix ?? "");
    setStatementDescriptor(properties.statement_descriptor ?? "");
    setAppProperties(properties.app_properties ?? {});
    setSendSmsDrinkReminder(Boolean(properties.send_sms_drink_reminder));
  }, [properties]);

  const changeMessage = useCallback((id: string, html: string) => {
    setMessages((current) => current.map((message) => (message.id === id ? { ...message, message: html } : message)));
  }, []);

  const setAppProperty = useCallback((key: string, value: unknown) => {
    setAppProperties((current) => ({ ...current, [key]: value }));
  }, []);

  const saveMessages = useCallback(async () => {
    if (!customerId) return;
    try {
      await axios.post("/api/setMessagesToGuests", {
        customer_id: customerId,
        messagesToGuests: messages.map((message) => ({
          id: message.id,
          message: message.message,
        })),
      });
      notification.success({ message: "Messages to guests updated." });
      await load();
    } catch (error) {
      generalErrorAlert(error, "Failed to save messages to guests.");
    }
  }, [customerId, messages, load]);

  const saveStoreHours = useCallback(async () => {
    if (!customerId) return;
    try {
      await axios.post("/api/updateStoreHours/", {
        customer_id: customerId,
        store_hours: storeHours,
      });
      notification.success({ message: "Store hours updated." });
      await load();
    } catch (error) {
      generalErrorAlert(error, "There was an error updating your store hours.");
    }
  }, [customerId, storeHours, load]);

  const saveProperties = useCallback(
    async (properties_to_save: Record<string, unknown>, label: string) => {
      if (!customerId) return;
      try {
        await axios.post("/api/setCustomerProperties", {
          customer_id: customerId,
          properties: properties_to_save,
        });
        notification.success({ message: `Saved ${label}.` });
        await load();
      } catch (error) {
        generalErrorAlert(error, `Failed to save ${label}.`);
      }
    },
    [customerId, load]
  );

  const saveOrderNumberPrefix = useCallback(
    () => saveProperties({ order_number_prefix: orderNumberPrefix }, "Order Number Prefix"),
    [saveProperties, orderNumberPrefix]
  );

  const saveStatementDescriptor = useCallback(
    () => saveProperties({ statement_descriptor: statementDescriptor }, "Statement Descriptor"),
    [saveProperties, statementDescriptor]
  );

  // order_num_daily_reset, order_num_digits and the drink-reminder toggle share
  // this save; app_properties is sent whole because the backend replaces it.
  const saveAppProperties = useCallback(
    () =>
      saveProperties(
        { app_properties: appProperties, send_sms_drink_reminder: sendSmsDrinkReminder },
        "App Properties"
      ),
    [saveProperties, appProperties, sendSmsDrinkReminder]
  );

  const uploadAsset = useCallback(
    async (assetType: AssetType, filesUploaded: unknown[]) => {
      try {
        await axios.post(ASSET_SAVE_ENDPOINTS[assetType], [{ filesUploaded }]);
        notification.success({ message: "Image uploaded." });
        await load();
      } catch (error) {
        generalErrorAlert(error, "Trouble uploading image. Please refresh and try again.");
      }
    },
    [load]
  );

  const deleteAsset = useCallback(
    async (assetType: AssetType) => {
      if (!customerId) return;
      try {
        await axios.post("/api/deleteCustomerAsset", { customerId, assetType });
        notification.success({ message: "Successfully deleted image." });
        await load();
      } catch (error) {
        generalErrorAlert(error, "Trouble deleting image. Please refresh and try again.");
      }
    },
    [customerId, load]
  );

  const disconnectFromBrand = useCallback(async () => {
    const brandParent = properties?.brand_parent;
    if (!brandParent) return;
    try {
      await axios.post("/api/disconnectFromBrand/", { source_customer_id: brandParent.customer_id });
      notification.success({ message: "Successfully disconnected." });
      await load();
    } catch (error) {
      generalErrorAlert(error, "An error occurred while disconnecting.");
    }
  }, [properties, load]);

  const brandParent = properties?.brand_parent ?? null;

  const canEditBranding = useMemo(
    () => Boolean(brandParent && allowedCustomerIds.includes(brandParent.customer_id)),
    [brandParent, allowedCustomerIds]
  );

  return {
    isLoading,
    // images
    mobileBannerUrl: customerFileUrl(properties?.banner_url),
    desktopBannerUrl: customerFileUrl(properties?.desktop_banner_url),
    logoUrl: customerFileUrl(properties?.app_properties?.logo_url),
    uploadAsset,
    deleteAsset,
    // branding
    brandParent,
    brandParentLogoUrl: customerFileUrl(brandParent?.logo_url),
    canEditBranding,
    disconnectFromBrand,
    // guest messages
    messages,
    changeMessage,
    saveMessages,
    // store hours
    storeHours,
    setStoreHours,
    saveStoreHours,
    // other settings
    orderNumberPrefix,
    setOrderNumberPrefix,
    saveOrderNumberPrefix,
    statementDescriptor,
    setStatementDescriptor,
    saveStatementDescriptor,
    appProperties,
    setAppProperty,
    sendSmsDrinkReminder,
    setSendSmsDrinkReminder,
    saveAppProperties,
    smsFeatureEnabled: Boolean(properties?.sms_notifications_feature_flag_enabled),
  };
};
